import { Box } from '@mui/material'
import { useTour } from '@reactour/tour'
import GroupCard from 'components/cards/groupCard/GroupCard'
import GroupLoader from 'components/loaders/GroupLoader'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import AddGroupPopup from 'components/popups/addGroup/AddGroupPopup'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import dayjs from 'dayjs'
import { Reorder } from 'framer-motion'
import { memo, useCallback, useState } from 'react'
import { clearUnread } from 'services/api/userSettings'
import { ThirdPartyAccountByEmail } from 'services/db/Queries/ThirdPartyCalendarsQuieries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { AccountDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  groupUnread,
  isGroupDefault,
  isGroupHidden,
  isGroupMuted
} from 'utils/api/userSettings'
import { prefixGoogleEventsGroup } from 'utils/constants'
import { fetchCalendarsFromGoogle } from 'utils/google_events'
import { groupToProvider, isConvertedGroup } from 'utils/groups'
import { isTrulyEmpty } from 'utils/object'

import { GroupsProps, LastUpdateMessagePerGroup } from './Groups'

interface GroupsListProps {
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc | undefined
  groupsForUI: Model<GroupDoc>[] | GroupDoc[]

  pendingGroup?: Model<GroupDoc>
  addGroupPopupState: boolean
  onAddGroupPopupState: (b: boolean) => void
  onCancelAdd: () => void
  handleCloseContextMenu?: () => void
  handleContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: GroupDoc
  ) => void
  topRef?: any
  lastUpdateMessages?: LastUpdateMessagePerGroup[]
  account: Model<AccountDoc>
}

export function GroupsList(props: GroupsListProps) {
  const {
    pendingGroup,
    addGroupPopupState,
    onAddGroupPopupState,
    onCancelAdd,
    topRef
  } = props

  const renderedGroups =
    props.groupsForUI.length > 0 ? (
      props.groupsForUI.map((row, index) => (
        <Reorder.Item
          as="div"
          key={row.id ?? row._id}
          value={row}
          dragListener={false}
        >
          <AddGroupPopup
            account={props.account}
            group={pendingGroup}
            open={
              pendingGroup !== undefined &&
              pendingGroup !== null &&
              pendingGroup._rev === row._rev
            }
            setOpen={onAddGroupPopupState}
            onClose={onCancelAdd}
            onClickAwayAction={onCancelAdd}
          >
            <div>
              <GroupRowMemo
                index={index}
                row={row}
                rowRev={row._rev}
                {...props}
              />
            </div>
          </AddGroupPopup>
        </Reorder.Item>
      ))
    ) : (
      <Box marginTop={3}>
        {[...Array(5)].map((_, i) => (
          <GroupLoader key={i} />
        ))}
      </Box>
    )

  return (
    <div style={{ width: '100%', paddingLeft: 8, paddingRight: 4 }}>
      {renderedGroups}
    </div>
  )
}

interface GroupRowProps extends GroupsProps {
  row: Model<GroupDoc> | GroupDoc
  rowRev?: string
  onCancelAdd: () => void
  handleCloseContextMenu?: () => void
  handleContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: GroupDoc
  ) => void
  handleGroupClick?: (group: GroupDoc) => void
  disableSelection?: boolean
  hideMetaData?: boolean
  lastUpdateMessages?: LastUpdateMessagePerGroup[]
  index?: number
}

export function GroupRow(props: GroupRowProps) {
  const calendar = useCalendar()
  const emptyClick = useCallback(() => {}, [])

  const reloadGoogleEvents = async (group: GroupDoc) => {
    if (group.SyncProblem != null) return
    const from = dayjs(
      new Date(Number(LocalStorage.get(LocalStorageKey.VISIBLE_DATE)))
    )
      .add(-1, 'month')
      .toISOString()
    const to = dayjs(
      new Date(Number(LocalStorage.get(LocalStorageKey.VISIBLE_DATE)))
    )
      .add(1, 'month')
      .toISOString()
    const googleAcc = await ThirdPartyAccountByEmail(
      group._id?.replace(prefixGoogleEventsGroup, '') ?? ''
    )
    fetchCalendarsFromGoogle(
      googleAcc.email,
      {
        access_token: googleAcc.token,
        expiry_date: googleAcc.tokenExpire,
        email: '',
        username: '',
        action: '',
        connected: false
      },
      from,
      to
    )
  }

  const calendarSync = useCalendarSync()
  const tour = useTour()
  const tfmTour = useTfmTourProvider()

  const click = () => {
    Logger.orange('group', props.row)
    LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)

    if (props.row._id === 'tempid') {
      //add google cal here
      if (calendarSync.showProviderPicker) calendarSync.showProviderPicker()
      return
    } else {
      if (!isTrulyEmpty(props.row._rev)) {
        clearUnread(props.row._id ?? '')
      }

      if (isConvertedGroup(props.row)) {
        reloadGoogleEvents(props.row)
      }
    }
    props.onCancelAdd()
    calendar.onGroupServer(props.row._id ?? '')

    if (props.handleGroupClick) {
      props.handleGroupClick(props.row)
    }

    if (tour.setSteps && tfmTour.getStepsByMode) {
      if (tfmTour.isPlaying) return
      const steps = tfmTour.getStepsByMode(ONBOARDING_MODE.FIRST_LOGIN)
      tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)
      tour.setSteps(steps)
      tour.setCurrentStep(0)
      tour.setIsOpen(steps.length > 0)
    }
  }

  return (
    <div
      id={props.index === 0 ? 'very-first-group' : undefined}
      onClick={click}
      style={{ width: '100%' }}
    >
      <GroupCard
        pinned={false}
        hideMetaData={props.hideMetaData}
        disableSelection={props.disableSelection}
        handleContextMenu={props.handleContextMenu}
        groupId={props.row._id}
        unread={groupUnread(props.userSettings, props.row._id ?? '')}
        userSettings={props.userSettings}
        group={props.row}
        muted={isGroupMuted(props.userSettings, props.row._id ?? '')}
        hidden={isGroupHidden(props.userSettings, props.row._id ?? '')}
        default={isGroupDefault(props.userSettings, props.row._id ?? '')}
        onClick={emptyClick}
        lastUpdateMessagePerGroup={props.lastUpdateMessages?.find((group) => {
          return group.groupId === props.row._id
        })}
      />
    </div>
  )
}

// Export memoized version to prevent re-renders when parent updates
export const GroupRowMemo = memo(GroupRow)
